import React from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Text,
  Box,
  Icon,
  Flex,
  Center,
  Button,
  SimpleGrid,
} from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'
import { ContentType } from '../../types/content/content'
import { ArrowLeftIcon } from '../../assets/icons'
import { statusColors } from '../../themes/components/tag'
import { useTranslation } from 'react-i18next'

interface ContentOptionsModalProps {
  isOpen: boolean
  onClose: () => void
  onBack: () => void
}

interface OptionItem {
  key: string
  labelKey: string
  onClick: () => void
}

const ContentOptionsModal: React.FC<ContentOptionsModalProps> = ({
  isOpen,
  onClose,
  onBack,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const handleOptionClick = (option: string) => {
    let selectedType: ContentType
    switch (option) {
      case 'skill':      selectedType = ContentType.Skills;     break
      case 'competence': selectedType = ContentType.Competence; break
      case 'task':       selectedType = ContentType.Task;       break
      case 'goal':       selectedType = ContentType.Goal;       break
      case 'note':       selectedType = ContentType.Note;       break
      case 'form':       selectedType = ContentType.Form;       break
      default:           selectedType = ContentType.Skills
    }
    navigate('/user/questionnaire/create', {
      state: { selectedType, questions: [] },
    })
    onClose()
  }

  const options: OptionItem[] = [
    { key: 'skill',       labelKey: 'content.types.skills',      onClick: () => handleOptionClick('skill') },
    { key: 'skill2',      labelKey: 'content.types.skills',      onClick: () => handleOptionClick('skill') },
    { key: 'competence',  labelKey: 'content.types.competence',  onClick: () => handleOptionClick('competence') },
    { key: 'task',        labelKey: 'content.types.task',        onClick: () => handleOptionClick('task') },
    { key: 'goal',        labelKey: 'content.types.goal',        onClick: () => handleOptionClick('goal') },
    { key: 'note',        labelKey: 'content.types.note',        onClick: () => handleOptionClick('note') },
    { key: 'form',        labelKey: 'content.types.form',        onClick: () => handleOptionClick('form') },
    {
      key: 'assessments',
      labelKey: 'content.types.assessments',
      onClick: () => window.open('https://hrmforce.com', '_blank', 'noopener,noreferrer'),
    },
  ]

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="2xl">
      <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(4px)" />
      <ModalContent borderRadius="16px" boxShadow="0 8px 32px rgba(0,0,0,0.12)" overflow="hidden">
        <ModalHeader pt="28px" pb="20px" px="32px">
          <Flex alignItems="center" justifyContent="space-between">
            <Button
              onClick={onBack}
              variant="ghost"
              size="sm"
              aria-label={t('common.actions.back')}
              borderRadius="8px"
              _hover={{ bg: 'solid.25' }}
              _focus={{ boxShadow: 'none', outline: 'none' }}
            >
              <Icon boxSize={5} color="solid.700" fill="none">
                <ArrowLeftIcon />
              </Icon>
            </Button>
            <Center flex="1">
              <Text fontSize="18px" fontWeight="700" color="solid.900">
                {t('content.modals.contentOptions.title')}
              </Text>
            </Center>
            <ModalCloseButton
              position="static"
              sx={{
                border: 'none',
                bg: 'transparent',
                _hover: { bg: 'solid.25', borderRadius: '6px' },
                _focus: { outline: 'none', boxShadow: 'none', border: 'none' },
              }}
            />
          </Flex>
        </ModalHeader>

        <ModalBody py="24px" px="32px" pb="32px">
          <SimpleGrid columns={4} spacing="12px">
            {options.map((option) => (
              <Box
                key={option.key}
                onClick={option.onClick}
                role="group"
                cursor="pointer"
                bg="solid.0"
                borderRadius="12px"
                border="1.5px solid"
                borderColor="solid.100"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                py="28px"
                px="12px"
                transition="all 0.2s"
                _hover={{
                  transform: 'translateY(-4px)',
                  bg: statusColors.warning.bg,
                  borderColor: statusColors.warning.border,
                  boxShadow: '0 8px 16px rgba(0,0,0,0.08)',
                }}
                _active={{
                  transform: 'translateY(0px)',
                  boxShadow: 'none',
                }}
              >
                <Text
                  fontSize="13px"
                  fontWeight="600"
                  color="solid.900"
                  textAlign="center"
                  lineHeight="1.3"
                  transition="color 0.2s"
                  _groupHover={{ color: statusColors.warning.color }}
                  noOfLines={2}
                >
                  {t(option.labelKey)}
                </Text>
              </Box>
            ))}
          </SimpleGrid>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export default ContentOptionsModal